import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface BillingInvoiceItem {
  constructionDate?: string;
  companyName?: string;
  productName: string;
  amount: number;
}

export interface BillingInvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  partnerName: string;
  items: BillingInvoiceItem[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string;
}

// 金額をフォーマット
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
  }).format(amount);
}

function formatNumber(amount: number): string {
  return new Intl.NumberFormat('ja-JP').format(amount);
}

// 日付をフォーマット
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}

function formatDateShort(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

// 自社情報
const COMPANY_INFO = {
  name: '株式会社セカイエ',
  postalCode: '〒530-0001',
  address: '大阪府大阪市北区梅田3丁目3-45 マルイト西梅田ビル5F',
  phone: 'TEL: 06-6536-8787',
  fax: 'FAX: 06-6536-8788',
  bankName: '三菱UFJ銀行',
  bankBranch: '梅田支店',
  bankType: '普通',
  bankNumber: '0119877',
  bankHolder: 'カ）セカイエ',
};

// HTMLテンプレートを生成（サンプルPDFフォーマットに準拠）
function createBillingInvoiceHTML(data: BillingInvoiceData): string {
  const itemRows = data.items.map(item => `
    <tr>
      <td style="border: 1px solid #999; padding: 6px 8px; text-align: center; font-size: 11px;">${formatDateShort(item.constructionDate)}</td>
      <td style="border: 1px solid #999; padding: 6px 8px; text-align: left; font-size: 11px;">${item.companyName || ''}</td>
      <td style="border: 1px solid #999; padding: 6px 8px; text-align: left; font-size: 11px;">${item.productName}</td>
      <td style="border: 1px solid #999; padding: 6px 8px; text-align: right; font-size: 11px;">${formatNumber(item.amount)}</td>
    </tr>
  `).join('');

  // 空行で埋める（最低10行にする）
  const emptyRows = Math.max(0, 10 - data.items.length);
  const emptyRowsHtml = Array(emptyRows).fill(`
    <tr>
      <td style="border: 1px solid #999; padding: 6px 8px; height: 24px;">&nbsp;</td>
      <td style="border: 1px solid #999; padding: 6px 8px;">&nbsp;</td>
      <td style="border: 1px solid #999; padding: 6px 8px;">&nbsp;</td>
      <td style="border: 1px solid #999; padding: 6px 8px;">&nbsp;</td>
    </tr>
  `).join('');

  return `
    <div id="billing-invoice-content" style="width: 794px; padding: 40px; font-family: 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif; background: white; color: #333; box-sizing: border-box;">
      <!-- ヘッダー -->
      <div style="text-align: center; margin-bottom: 24px;">
        <h1 style="font-size: 28px; margin: 0; letter-spacing: 8px; border-bottom: 3px double #333; display: inline-block; padding-bottom: 4px;">請　求　書</h1>
      </div>

      <!-- 上部情報 -->
      <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
        <!-- 左: 宛先 -->
        <div style="flex: 1;">
          <p style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold; border-bottom: 1px solid #333; display: inline-block; padding-bottom: 2px;">
            ${data.partnerName}　御中
          </p>
          <div style="margin-top: 16px; background: #f0f4ff; border: 1px solid #2563eb; border-radius: 4px; padding: 12px;">
            <p style="margin: 0; font-size: 12px; color: #666;">ご請求金額（税込）</p>
            <p style="margin: 4px 0 0 0; font-size: 24px; font-weight: bold; color: #2563eb;">￥${formatNumber(data.totalAmount)}-</p>
          </div>
        </div>

        <!-- 右: 自社情報 -->
        <div style="text-align: right; margin-left: 40px;">
          <p style="margin: 0; font-size: 11px; color: #666;">請求書番号: ${data.invoiceNumber}</p>
          <p style="margin: 4px 0; font-size: 11px; color: #666;">請求日: ${formatDate(data.invoiceDate)}</p>
          ${data.dueDate ? `<p style="margin: 4px 0; font-size: 11px; color: #666;">お支払期限: ${formatDate(data.dueDate)}</p>` : ''}
          <div style="margin-top: 12px; border-top: 1px solid #ddd; padding-top: 8px;">
            <p style="margin: 0; font-size: 14px; font-weight: bold;">${COMPANY_INFO.name}</p>
            <p style="margin: 4px 0 0; font-size: 11px; color: #666;">${COMPANY_INFO.postalCode}</p>
            <p style="margin: 2px 0 0; font-size: 11px; color: #666;">${COMPANY_INFO.address}</p>
            <p style="margin: 2px 0 0; font-size: 11px; color: #666;">${COMPANY_INFO.phone}</p>
            <p style="margin: 2px 0 0; font-size: 11px; color: #666;">${COMPANY_INFO.fax}</p>
          </div>
        </div>
      </div>

      <!-- 明細テーブル -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
        <thead>
          <tr style="background: #2563eb; color: white;">
            <th style="border: 1px solid #2563eb; padding: 8px; text-align: center; font-size: 12px; width: 12%;">施工日</th>
            <th style="border: 1px solid #2563eb; padding: 8px; text-align: center; font-size: 12px; width: 30%;">企業名</th>
            <th style="border: 1px solid #2563eb; padding: 8px; text-align: center; font-size: 12px; width: 38%;">商材</th>
            <th style="border: 1px solid #2563eb; padding: 8px; text-align: center; font-size: 12px; width: 20%;">金額</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
          ${emptyRowsHtml}
        </tbody>
      </table>

      <!-- 合計テーブル -->
      <div style="display: flex; justify-content: flex-end; margin-bottom: 24px;">
        <table style="width: 280px; border-collapse: collapse;">
          <tr>
            <td style="padding: 6px 12px; text-align: left; font-size: 12px; color: #666; border-bottom: 1px solid #ddd;">小計</td>
            <td style="padding: 6px 12px; text-align: right; font-size: 12px; border-bottom: 1px solid #ddd;">￥${formatNumber(data.subtotal)}</td>
          </tr>
          <tr>
            <td style="padding: 6px 12px; text-align: left; font-size: 12px; color: #666; border-bottom: 1px solid #ddd;">消費税（${data.taxRate}%）</td>
            <td style="padding: 6px 12px; text-align: right; font-size: 12px; border-bottom: 1px solid #ddd;">￥${formatNumber(data.taxAmount)}</td>
          </tr>
          <tr style="background: #f0f4ff;">
            <td style="padding: 8px 12px; text-align: left; font-size: 13px; font-weight: bold; border: 1px solid #2563eb;">合計（税込）</td>
            <td style="padding: 8px 12px; text-align: right; font-size: 15px; font-weight: bold; color: #2563eb; border: 1px solid #2563eb;">￥${formatNumber(data.totalAmount)}</td>
          </tr>
        </table>
      </div>

      ${data.notes ? `
      <!-- 備考 -->
      <div style="background: #f8fafc; padding: 12px; border-radius: 4px; margin-bottom: 20px; border: 1px solid #e5e7eb;">
        <p style="margin: 0 0 4px 0; font-weight: bold; font-size: 11px; color: #666;">備考</p>
        <p style="margin: 0; font-size: 11px; white-space: pre-wrap;">${data.notes}</p>
      </div>
      ` : ''}

      <!-- 振込先 -->
      <div style="border: 1px solid #ddd; border-radius: 4px; padding: 12px; background: #fafafa;">
        <p style="margin: 0 0 8px 0; font-weight: bold; font-size: 12px;">お振込先</p>
        <table style="font-size: 11px;">
          <tr><td style="padding: 2px 12px 2px 0; color: #666;">銀行名</td><td>${COMPANY_INFO.bankName}</td></tr>
          <tr><td style="padding: 2px 12px 2px 0; color: #666;">支店名</td><td>${COMPANY_INFO.bankBranch}</td></tr>
          <tr><td style="padding: 2px 12px 2px 0; color: #666;">口座種別</td><td>${COMPANY_INFO.bankType}</td></tr>
          <tr><td style="padding: 2px 12px 2px 0; color: #666;">口座番号</td><td>${COMPANY_INFO.bankNumber}</td></tr>
          <tr><td style="padding: 2px 12px 2px 0; color: #666;">口座名義</td><td>${COMPANY_INFO.bankHolder}</td></tr>
        </table>
      </div>
    </div>
  `;
}

// 請求書PDFを生成
export async function generateBillingInvoicePDF(
  data: BillingInvoiceData
): Promise<{ blob: Blob; base64: string }> {
  const html = createBillingInvoiceHTML(data);

  const container = document.createElement('div');
  container.innerHTML = html;
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  const invoiceElement = container.querySelector('#billing-invoice-content') as HTMLElement;

  try {
    const canvas = await html2canvas(invoiceElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let finalWidth = imgWidth;
    let finalHeight = imgHeight;

    if (imgHeight > pageHeight - 20) {
      finalHeight = pageHeight - 20;
      finalWidth = (canvas.width * finalHeight) / canvas.height;
    }

    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 10, 10, finalWidth, finalHeight);

    const blob = doc.output('blob');
    const base64 = doc.output('datauristring').split(',')[1];

    return { blob, base64 };
  } finally {
    document.body.removeChild(container);
  }
}

// PDFダウンロード（既存をre-export）
export { downloadInvoicePDF, createPDFPreviewURL } from './invoice-pdf';
